import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  AbgleichAuswahl,
  AbgleichErgebnis,
  AktivitaetsGruppe,
  AktivitaetsVerlauf,
  Anlagenteil,
  ExcelAiAnalysis,
  ExcelAnalyseResult,
  ExcelPreview,
  ImportRegeln,
  ImportVorschau,
  MessstelleDetail,
  MessstellenSammelErgebnis,
  MessstelleBild,
  MessstelleDuplikatErgebnis,
  MessstelleKategorie,
  MessstellePruefpunkt,
  MessstellePruefprotokoll,
  MessstellenListeDetail,
  MessstellenListeSummary,
  ListenStruktur,
  ListenStrukturVorlage,
  ListenTyp,
  Pruefergebnis,
  PruefpunktErgebnis,
  RegelErgebnis,
  RegelOperator,
  SammelZuordnungErgebnis,
  SammelZuordnungRegeln,
  SignalStatus,
  ZeilenEntscheidung,
  ZeilenRelevanzErgebnis
} from '../Models/messstelle.model';
import {
  OfflinePaket,
  SyncAenderung,
  SyncErgebnis
} from '../Models/offline.model';

/**
 * Import und Abruf von Messstellenlisten (Bereich Inbetriebnahme).
 *
 * Der Import ist zweistufig: erst `preview` (liest die Datei nur), dann `import`
 * mit dem bestätigten Arbeitsblatt und der bestätigten Kopfzeile. Die Datei wird
 * dabei zweimal hochgeladen – dafür muss der Server nichts zwischenspeichern.
 */
@Injectable({ providedIn: 'root' })
export class InbetriebnahmeService {
  private baseUrl = environment.apiBaseUrl + '/inbetriebnahme';

  constructor(private http: HttpClient) { }

  preview(file: File): Observable<ExcelPreview> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<ExcelPreview>(`${this.baseUrl}/preview`, form);
  }

  /**
   * Import einer Messstellenliste. Wird eine `projektId` mitgegeben, gehört die
   * entstehende Inbetriebnahme zu diesem Projekt – der Kunde ergibt sich dann
   * serverseitig aus dem Projekt.
   */
  /**
   * Liest die Datei und lässt zusätzlich die Struktur erkennen (Blatt, Kopfbereich,
   * Datenstart, Spaltenbedeutung). Schlägt die Erkennung fehl, kommt trotzdem eine
   * Antwort mit `aiAvailable: false` und der normalen Vorschau.
   */
  analyze(file: File): Observable<ExcelAnalyseResult> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<ExcelAnalyseResult>(`${this.baseUrl}/analyze`, form);
  }

  /**
   * Import einer Messstellenliste. Wird eine `projektId` mitgegeben, gehört die
   * entstehende Inbetriebnahme zu diesem Projekt – der Kunde ergibt sich dann
   * serverseitig aus dem Projekt.
   *
   * `analysis` ist die – gegebenenfalls vom Nutzer korrigierte – erkannte Struktur.
   * Ohne sie läuft der Import wie bisher über eine einzelne Kopfzeile.
   */
  import(
    file: File,
    sheetName: string,
    headerRowNumber: number,
    name: string,
    customerId: number | null,
    projektId: number | null = null,
    analysis: ExcelAiAnalysis | null = null,
    regeln: ImportRegeln | null = null,
    /**
     * Die Zeilenentscheidungen aus der Vorschau. Leer = wie bisher wird jede
     * Zeile importiert; der Ablauf ohne Zeilenprüfung bleibt gültig.
     */
    entscheidungen: ZeilenEntscheidung[] = []
  ): Observable<MessstellenListeDetail> {
    const form = new FormData();
    form.append('file', file);
    form.append('sheetName', sheetName);
    form.append('headerRowNumber', String(headerRowNumber));
    form.append('name', name);
    if (customerId != null) {
      form.append('customerId', String(customerId));
    }
    if (projektId != null) {
      form.append('projektId', String(projektId));
    }
    if (analysis != null) {
      form.append('analysisJson', JSON.stringify(analysis));
    }
    if (regeln != null) {
      form.append('regelnJson', JSON.stringify(regeln));
    }
    if (entscheidungen.length > 0) {
      form.append('entscheidungenJson', JSON.stringify(entscheidungen));
    }
    return this.http.post<MessstellenListeDetail>(`${this.baseUrl}/import`, form);
  }

  /**
   * Rechnet den Import vollständig durch, ohne etwas zu speichern.
   *
   * Antwortet mit den endgültigen Spaltenüberschriften, echten Zeilen und der
   * Kategorie- bzw. Anlagenteil-Zuordnung, die die Regeln erzeugen. Gerechnet
   * wird serverseitig auf demselben Weg wie beim Import – eine Vorschau, die
   * anders rechnet als das Ergebnis, wäre schlimmer als gar keine.
   */
  vorschau(
    file: File,
    sheetName: string,
    headerRowNumber: number,
    analysis: ExcelAiAnalysis | null,
    regeln: ImportRegeln | null,
    maxZeilen = 25,
    /**
     * Projekt, in das importiert wird. Nur damit der Server bereits bekannte
     * Anlagenteil-Benennungen vorschlagen kann ("UKR12" -> "Absackung");
     * gespeichert wird bei der Vorschau nichts.
     */
    projektId: number | null = null
  ): Observable<ImportVorschau> {
    const form = new FormData();
    form.append('file', file);
    form.append('sheetName', sheetName);
    form.append('headerRowNumber', String(headerRowNumber));
    form.append('maxZeilen', String(maxZeilen));
    if (analysis != null) {
      form.append('analysisJson', JSON.stringify(analysis));
    }
    if (regeln != null) {
      form.append('regelnJson', JSON.stringify(regeln));
    }
    if (projektId != null) {
      form.append('projektId', String(projektId));
    }
    return this.http.post<ImportVorschau>(`${this.baseUrl}/vorschau`, form);
  }


  /**
   * Beurteilt JEDE Zeile der Datei: echter Datensatz, oder Gruppenkopf /
   * mechanisches Bauteil / Hilfszeile?
   *
   * Ein eigener Aufruf, weil er Geld kostet – er wird nur ausgelöst, wenn der
   * Benutzer ihn will, und nicht bei jedem Tastendruck in der Vorschau.
   *
   * @param entscheidungen Was der Benutzer bereits selbst gesetzt hat. Diese
   * Zeilen werden nicht erneut beurteilt.
   * @param listeId Beim Aktualisieren einer bestehenden Liste deren Id: dann
   * werden unveränderte Zeilen aus den früheren Entscheidungen übernommen,
   * ohne die KI zu fragen.
   */
  zeilenRelevanz(
    file: File,
    sheetName: string,
    headerRowNumber: number,
    analysis: ExcelAiAnalysis | null,
    entscheidungen: ZeilenEntscheidung[] = [],
    listeId: number | null = null,
    /** "Erneut prüfen": gemerkte KI-Urteile des Servers nicht wiederverwenden. */
    erneutPruefen = false
  ): Observable<ZeilenRelevanzErgebnis> {
    const form = new FormData();
    form.append('file', file);
    form.append('sheetName', sheetName);
    form.append('headerRowNumber', String(headerRowNumber));
    if (analysis != null) {
      form.append('analysisJson', JSON.stringify(analysis));
    }
    if (entscheidungen.length > 0) {
      form.append('entscheidungenJson', JSON.stringify(entscheidungen));
    }
    if (listeId != null) {
      form.append('listeId', String(listeId));
    }
    if (erneutPruefen) {
      form.append('erneutPruefen', 'true');
    }
    return this.http.post<ZeilenRelevanzErgebnis>(`${this.baseUrl}/relevanz`, form);
  }

  /** Ohne `projektId` alle sichtbaren Listen, sonst nur die des Projekts. */
  getListen(projektId: number | null = null): Observable<MessstellenListeSummary[]> {
    const url = projektId != null
      ? `${this.baseUrl}/listen?projektId=${projektId}`
      : `${this.baseUrl}/listen`;
    return this.http.get<MessstellenListeSummary[]>(url);
  }

  getListe(id: number): Observable<MessstellenListeDetail> {
    return this.http.get<MessstellenListeDetail>(`${this.baseUrl}/listen/${id}`);
  }

  /** Ordnet eine bestehende Inbetriebnahme nachträglich einem Projekt zu. */
  setProjekt(listeId: number, projektId: number): Observable<MessstellenListeDetail> {
    return this.http.put<MessstellenListeDetail>(
      `${this.baseUrl}/listen/${listeId}/projekt`,
      { projektId }
    );
  }

  deleteListe(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/listen/${id}`);
  }

  /** Nur der Name ändert sich. Antwortet mit der frischen Übersichtszeile. */
  umbenenneListe(id: number, name: string): Observable<MessstellenListeSummary> {
    return this.http.put<MessstellenListeSummary>(
      `${this.baseUrl}/listen/${id}/name`,
      { name }
    );
  }

  /**
   * Vollständige Kopie einer Inbetriebnahme – Messstellen, Prüfpunkte,
   * Ergebnisse, Verlauf, Fotos und Dateien, unter neuen Ids. Antwortet mit
   * der Übersichtszeile der Kopie. Bewusst online-only.
   */
  dupliziereListe(id: number): Observable<MessstellenListeSummary> {
    return this.http.post<MessstellenListeSummary>(
      `${this.baseUrl}/listen/${id}/duplizieren`,
      {}
    );
  }

  /**
   * Eine einzelne Messstelle aus einer Inbetriebnahme löschen – samt
   * Prüfpunkten, Verlauf, Fotos und Geräte-Dateien. Bewusst online-only.
   */
  deleteMessstelle(listeId: number, messstelleId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/listen/${listeId}/messstellen/${messstelleId}`);
  }

  /**
   * Mehrere Messstellen löschen – jede für sich mit derselben Löschlogik wie
   * `deleteMessstelle`. Das Ergebnis nennt je Messstelle, ob es geklappt hat.
   * Bewusst online-only.
   */
  loescheMessstellen(listeId: number, messstelleIds: number[]): Observable<MessstellenSammelErgebnis> {
    return this.http.post<MessstellenSammelErgebnis>(
      `${this.baseUrl}/listen/${listeId}/messstellen/sammel-loeschen`,
      { messstelleIds }
    );
  }

  /**
   * Alle Prüfpunkte mehrerer Messstellen auf „bestanden" setzen – mit exakt
   * derselben Serverlogik wie die Einzelprüfung (`updatePruefpunkt`):
   * Quittierung, Änderungsstand und Protokolleintrag je Prüfpunkt.
   */
  pruefeMessstellen(listeId: number, messstelleIds: number[]): Observable<MessstellenSammelErgebnis> {
    return this.http.post<MessstellenSammelErgebnis>(
      `${this.baseUrl}/listen/${listeId}/messstellen/sammel-pruefen`,
      { messstelleIds }
    );
  }

  /**
   * Eine Messstelle innerhalb ihrer Liste vollständig kopieren – Werte,
   * Kategorie, Anlagenteil, Prüfpunkte mit Ergebnissen, Verlauf, Fotos und
   * Dateien – unter neuer Id und mit der übergebenen Kennzeichnung. Bewusst
   * online-only; eine doppelte Kennzeichnung beantwortet der Server mit 409.
   */
  dupliziereMessstelle(
    listeId: number,
    messstelleId: number,
    kennzeichnung: string
  ): Observable<MessstelleDuplikatErgebnis> {
    return this.http.post<MessstelleDuplikatErgebnis>(
      `${this.baseUrl}/listen/${listeId}/messstellen/${messstelleId}/duplizieren`,
      { kennzeichnung }
    );
  }

  // ── Anlagenteile ──
  //
  // Ein Anlagenteil ist eine einfache Gruppierung innerhalb EINER Liste
  // ("Absackanlage", "Kessel") – kein Anlagenstammbaum. Gebraucht wird ein
  // Filter- und Suchkriterium für Übersicht und spätere Auswertungen.

  getAnlagenteile(listeId: number): Observable<Anlagenteil[]> {
    return this.http.get<Anlagenteil[]>(`${this.baseUrl}/listen/${listeId}/anlagenteile`);
  }

  createAnlagenteil(
    listeId: number,
    name: string,
    sortOrder = 0
  ): Observable<Anlagenteil> {
    return this.http.post<Anlagenteil>(
      `${this.baseUrl}/listen/${listeId}/anlagenteile`,
      { name, sortOrder }
    );
  }

  updateAnlagenteil(
    anlagenteilId: number,
    name: string,
    sortOrder = 0
  ): Observable<Anlagenteil> {
    return this.http.put<Anlagenteil>(
      `${this.baseUrl}/anlagenteile/${anlagenteilId}`,
      { name, sortOrder }
    );
  }

  /**
   * Löscht einen Anlagenteil. Die Messstellen bleiben bestehen und verlieren
   * nur ihre Zuordnung.
   */
  deleteAnlagenteil(anlagenteilId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/anlagenteile/${anlagenteilId}`);
  }

  /** Anlagenteil einer einzelnen Messstelle setzen; `null` hebt die Zuordnung auf. */
  setAnlagenteil(
    messstelleId: number,
    anlagenteilId: number | null
  ): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/anlagenteil`,
      { anlagenteilId }
    );
  }

  /**
   * Ordnet über eine Filterregel viele Messstellen auf einmal einem
   * Anlagenteil zu.
   *
   * Mit `nurVorschau: true` wird nichts geschrieben – die Antwort enthält nur
   * die Trefferzahl und einige Beispiele. Genau so sieht der Benutzer vor dem
   * Übernehmen, was die Regel tut.
   */
  anlagenteilZuordnen(
    listeId: number,
    regel: {
      feld: string;
      operator: RegelOperator;
      wert: string;
      anlagenteilId?: number | null;
      neuerName?: string | null;
      nurVorschau?: boolean;
      ueberschreiben?: boolean;
    }
  ): Observable<RegelErgebnis> {
    return this.http.post<RegelErgebnis>(
      `${this.baseUrl}/listen/${listeId}/anlagenteil-zuordnung`,
      regel
    );
  }

  /**
   * Dasselbe für Kategorien – der Weg, auf dem ein KI-Vorschlag nach
   * Bestätigung wirksam wird. Die Prüfpunkte entstehen über dieselbe Logik
   * wie bei der einzelnen Zuweisung auf der Detailseite.
   */
  kategorieZuordnen(
    listeId: number,
    regel: {
      feld: string;
      operator: RegelOperator;
      wert: string;
      kategorieId: number;
      nurVorschau?: boolean;
      ueberschreiben?: boolean;
    }
  ): Observable<RegelErgebnis> {
    return this.http.post<RegelErgebnis>(
      `${this.baseUrl}/listen/${listeId}/kategorie-zuordnung`,
      regel
    );
  }

  /**
   * Mehrere Kategorie- bzw. Anlagenteil-Regeln auf einmal auf eine bestehende
   * Liste anwenden – ein Request, serverseitig eine Transaktion.
   *
   * Mit `nurVorschau: true` wird nur gerechnet: Treffer je Regel, betroffene
   * Messstellen und Beispiele.
   */
  sammelZuordnung(
    listeId: number,
    regeln: SammelZuordnungRegeln,
    nurVorschau: boolean
  ): Observable<SammelZuordnungErgebnis> {
    return this.http.post<SammelZuordnungErgebnis>(
      `${this.baseUrl}/listen/${listeId}/sammel-zuordnung`,
      { ...regeln, nurVorschau }
    );
  }

  /**
   * Ergänzt eine Messstelle von Hand.
   *
   * Die Schlüssel sind Standardfelder, keine Excel-Spaltennamen – der Server
   * legt fehlende Spalten für die ganze Liste an.
   */
  createMessstelle(
    listeId: number,
    werte: { [standardFeld: string]: string },
    kategorieId: number | null,
    anlagenteilId: number | null
  ): Observable<MessstelleDetail> {
    return this.http.post<MessstelleDetail>(
      `${this.baseUrl}/listen/${listeId}/messstellen`,
      { werte, kategorieId, anlagenteilId }
    );
  }

  // ── Manuell erstellte Listen: Struktur und Vorlagen ──
  //
  // Eine manuell erstellte Liste ist dieselbe Inbetriebnahme wie eine
  // importierte; sie trägt zusätzlich ihre Spaltenstruktur. Der Excel-Import
  // bleibt davon unberührt.

  /**
   * Leere Liste erstellen – aus einer Vorlage (`vorlageId`) oder mit eigener
   * Struktur (`struktur`). Die Liste erhält eine eigenständige Kopie der
   * Struktur; die Vorlage kann danach geändert oder gelöscht werden.
   */
  createManuelleListe(request: {
    projektId?: number | null;
    customerId?: number | null;
    name: string;
    vorlageId?: number | null;
    listenTyp?: ListenTyp | null;
    struktur?: ListenStruktur | null;
    alsVorlageSpeichern?: boolean;
    vorlagenName?: string | null;
    vorlagenBeschreibung?: string | null;
  }): Observable<MessstellenListeDetail> {
    return this.http.post<MessstellenListeDetail>(`${this.baseUrl}/listen/manuell`, request);
  }

  getStruktur(listeId: number): Observable<ListenStruktur> {
    return this.http.get<ListenStruktur>(`${this.baseUrl}/listen/${listeId}/struktur`);
  }

  /**
   * Struktur einer manuell erstellten Liste ändern. Spalten werden über ihre
   * stabile Id zugeordnet. Antwortet der Server mit 409, verlangt er eine
   * Bestätigung (`loeschenBestaetigt`) oder meldet Kennzeichnungsprobleme.
   */
  updateStruktur(
    listeId: number,
    struktur: ListenStruktur,
    loeschenBestaetigt: string[] = []
  ): Observable<MessstellenListeDetail> {
    return this.http.put<MessstellenListeDetail>(
      `${this.baseUrl}/listen/${listeId}/struktur`,
      { struktur, loeschenBestaetigt }
    );
  }

  /** Struktur einer bestehenden manuellen Liste als neue Vorlage ablegen. */
  listeAlsVorlage(listeId: number, name: string, beschreibung: string | null = null): Observable<ListenStrukturVorlage> {
    return this.http.post<ListenStrukturVorlage>(
      `${this.baseUrl}/listen/${listeId}/als-vorlage`,
      { name, beschreibung }
    );
  }

  getStrukturVorlagen(): Observable<ListenStrukturVorlage[]> {
    return this.http.get<ListenStrukturVorlage[]>(`${this.baseUrl}/struktur-vorlagen`);
  }

  getStrukturVorlage(id: number): Observable<ListenStrukturVorlage> {
    return this.http.get<ListenStrukturVorlage>(`${this.baseUrl}/struktur-vorlagen/${id}`);
  }

  createStrukturVorlage(request: {
    name: string;
    beschreibung?: string | null;
    listenTyp: ListenTyp;
    struktur: ListenStruktur;
  }): Observable<ListenStrukturVorlage> {
    return this.http.post<ListenStrukturVorlage>(`${this.baseUrl}/struktur-vorlagen`, request);
  }

  updateStrukturVorlage(id: number, request: {
    name: string;
    beschreibung?: string | null;
    listenTyp: ListenTyp;
    struktur: ListenStruktur;
  }): Observable<ListenStrukturVorlage> {
    return this.http.put<ListenStrukturVorlage>(`${this.baseUrl}/struktur-vorlagen/${id}`, request);
  }

  dupliziereStrukturVorlage(id: number): Observable<ListenStrukturVorlage> {
    return this.http.post<ListenStrukturVorlage>(`${this.baseUrl}/struktur-vorlagen/${id}/duplizieren`, {});
  }

  /** Löscht eine Vorlage. Bestehende Listen bleiben unverändert. */
  deleteStrukturVorlage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/struktur-vorlagen/${id}`);
  }

  // ── Abgleich mit einer neueren Excel-Datei ──

  /**
   * Vergleicht die Liste mit einer neueren Datei und liefert eine VORSCHAU.
   * Es wird nichts gespeichert.
   */
  /**
   * @param vergleichsSpalte Spalte der neuen Datei, über die zugeordnet werden
   * soll. Nur nötig, wenn der Server `matchingUnsicher` gemeldet hat – sonst
   * findet er die Kennzeichnung über die Feldzuordnung der Liste selbst.
   */
  abgleich(
    listeId: number,
    file: File,
    vergleichsSpalte?: string | null
  ): Observable<AbgleichErgebnis> {
    const form = new FormData();
    form.append('file', file);
    if (vergleichsSpalte) {
      form.append('vergleichsSpalte', vergleichsSpalte);
    }
    return this.http.post<AbgleichErgebnis>(
      `${this.baseUrl}/listen/${listeId}/abgleich`,
      form
    );
  }

  /**
   * Übernimmt die ausgewählten Zeilen des Abgleichs.
   *
   * Die Datei wird ein zweites Mal hochgeladen – der Server hält bewusst
   * keinen Zustand zwischen den beiden Aufrufen. Entfallene Messstellen
   * werden nur gelöscht, wenn sie in `auswahl.entfalleneMessstellen` einzeln
   * angehakt wurden – nie stillschweigend.
   */
  abgleichUebernehmen(
    listeId: number,
    file: File,
    auswahl: AbgleichAuswahl,
    vergleichsSpalte?: string | null
  ): Observable<MessstellenListeDetail> {
    const form = new FormData();
    form.append('file', file);
    form.append('auswahlJson', JSON.stringify(auswahl));
    // Muss dieselbe sein wie in der Vorschau – sonst übernähme der Server
    // einen anderen Vergleich als den, den der Benutzer gesehen hat.
    if (vergleichsSpalte) {
      form.append('vergleichsSpalte', vergleichsSpalte);
    }
    return this.http.post<MessstellenListeDetail>(
      `${this.baseUrl}/listen/${listeId}/abgleich/uebernehmen`,
      form
    );
  }

  // ── Kategorien und Prüfpunkte ──

  getKategorien(): Observable<MessstelleKategorie[]> {
    return this.http.get<MessstelleKategorie[]>(`${this.baseUrl}/kategorien`);
  }



  getMessstelle(id: number): Observable<MessstelleDetail> {
    return this.http.get<MessstelleDetail>(`${this.baseUrl}/messstellen/${id}`);
  }

  /**
   * Kategorie setzen oder (mit `null`) entfernen. Antwortet mit der frisch
   * aufgebauten Messstelle inklusive angeglichener Prüfpunkte – die Seite muss
   * danach nichts nachladen.
   */
  setKategorie(messstelleId: number, kategorieId: number | null): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/kategorie`,
      { kategorieId }
    );
  }

  /**
   * Speichert die auf der Detailseite bearbeiteten allgemeinen Daten.
   *
   * Schlüssel sind Standardfelder ("AKS/KKS", "Messbereich Min", …), nicht
   * Excel-Spaltennamen. Der Server ordnet sie der jeweiligen Spalte zu und
   * legt sie an, falls die Datei sie noch nicht hatte. Ein leerer Wert löscht
   * den Eintrag.
   */
  updateWerte(
    messstelleId: number,
    werte: { [standardFeld: string]: string }
  ): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/werte`,
      { werte }
    );
  }

  /**
   * Ergebnis und Bemerkung eines Prüfpunkts.
   *
   * `ergebnis = null` lässt das Ergebnis unverändert – wichtig, damit das
   * Speichern einer Bemerkung es nicht zurücksetzt. Bei "nicht bestanden"
   * verlangt der Server eine Fehlerbeschreibung.
   */
  updatePruefpunkt(
    pruefpunktId: number,
    ergebnis: PruefpunktErgebnis | null,
    bemerkung: string | null
  ): Observable<MessstellePruefpunkt> {
    return this.http.put<MessstellePruefpunkt>(
      `${this.baseUrl}/pruefpunkte/${pruefpunktId}`,
      { ergebnis, bemerkung }
    );
  }

  // ── Gesamtergebnis, Signalstatus und Prüfverlauf ──

  /**
   * Gesamtergebnis der Messstelle. Immer speicherbar – eine
   * Fehlerbeschreibung verlangt nur der einzelne Prüfpunkt.
   *
   * Antwortet mit der frisch geladenen Messstelle – inklusive des soeben
   * entstandenen Protokolleintrags.
   */
  setPruefergebnis(
    messstelleId: number,
    pruefergebnis: Pruefergebnis
  ): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/pruefergebnis`,

      // Die Notiz bewusst NICHT mitschicken: sie hat einen eigenen Endpunkt,
      // und ein fehlendes Feld lässt der Server unangetastet.
      { pruefergebnis }
    );
  }

  /**
   * Die allgemeine Notiz zur Messstelle setzen. Leerer Text entfernt sie.
   *
   * Eigener Endpunkt, damit die Notiz das Prüfergebnis nicht berührt – und
   * umgekehrt.
   */
  setMessstelleNotiz(
    messstelleId: number,
    notiz: string | null
  ): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/notiz`,
      { notiz }
    );
  }

  /** Workflow-Status der Messstelle setzen. */
  setSignalStatus(
    messstelleId: number,
    signalStatus: SignalStatus,
    bemerkung: string | null = null
  ): Observable<MessstelleDetail> {
    return this.http.put<MessstelleDetail>(
      `${this.baseUrl}/messstellen/${messstelleId}/signalstatus`,
      { signalStatus, bemerkung }
    );
  }

  /** Der vollständige Aktivitätsverlauf einer Messstelle, neueste zuerst. */
  getPruefprotokoll(
    messstelleId: number
  ): Observable<MessstellePruefprotokoll[]> {
    return this.http.get<MessstellePruefprotokoll[]>(
      `${this.baseUrl}/messstellen/${messstelleId}/pruefprotokoll`
    );
  }

  /**
   * Der Aktivitätsverlauf – gefiltert und seitenweise.
   *
   * Gefiltert wird serverseitig, weil eine lange bearbeitete Messstelle
   * hunderte Einträge sammelt. Die alle zu übertragen, nur um zwanzig
   * anzuzeigen, ist auf einem Tablet im Feld spürbar.
   *
   * Die Zeitgrenzen gehen als UTC hinaus: die Zeitzone des Benutzers kennt
   * nur der Browser. Leere Filter werden weggelassen, damit der Server sie
   * nicht als "leerer Text" auswerten muss.
   */
  getAktivitaeten(
    messstelleId: number,
    filter: {
      gruppe?: AktivitaetsGruppe | null;
      person?: string | null;
      vonUtc?: string | null;
      bisUtc?: string | null;
      suche?: string | null;
      skip?: number;
      take?: number;
    } = {}
  ): Observable<AktivitaetsVerlauf> {

    let params = new HttpParams();

    if (filter.gruppe != null) {
      params = params.set('gruppe', String(filter.gruppe));
    }

    if (filter.person) {
      params = params.set('person', filter.person);
    }

    if (filter.vonUtc) {
      params = params.set('vonUtc', filter.vonUtc);
    }

    if (filter.bisUtc) {
      params = params.set('bisUtc', filter.bisUtc);
    }

    if (filter.suche) {
      params = params.set('suche', filter.suche);
    }

    if (filter.skip) {
      params = params.set('skip', String(filter.skip));
    }

    if (filter.take) {
      params = params.set('take', String(filter.take));
    }

    return this.http.get<AktivitaetsVerlauf>(
      `${this.baseUrl}/messstellen/${messstelleId}/aktivitaeten`,
      { params }
    );
  }

  // ── Bilder zur Messstelle ──

  /**
   * Lädt ein Foto hoch. `file` kommt entweder aus der Kamera oder aus
   * Galerie/Explorer – für den Server ist das dasselbe multipart-Feld.
   */
  uploadBild(
    messstelleId: number,
    file: File,
    beschreibung: string | null
  ): Observable<MessstelleBild> {
    const form = new FormData();
    form.append('file', file, file.name);

    if (beschreibung) {
      form.append('beschreibung', beschreibung);
    }

    return this.http.post<MessstelleBild>(
      `${this.baseUrl}/messstellen/${messstelleId}/bilder`,
      form
    );
  }

  /**
   * Holt die Bilddaten als Blob.
   *
   * Nötig, weil ein `<img src>` den Bearer-Token nicht mitschickt – nur
   * HttpClient-Aufrufe laufen durch den AuthInterceptor.
   */
  getBildBlob(bildId: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/bilder/${bildId}`,
      { responseType: 'blob' }
    );
  }

  /** Beschreibung bzw. Kommentar eines Bildes ändern. */
  updateBildBeschreibung(
    bildId: number,
    beschreibung: string | null
  ): Observable<MessstelleBild> {
    return this.http.put<MessstelleBild>(
      `${this.baseUrl}/bilder/${bildId}/beschreibung`,
      { beschreibung }
    );
  }

  deleteBild(bildId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/bilder/${bildId}`);
  }

  createPruefpunkt(
    messstelleId: number,
    bezeichnung: string,
    section: string | null
  ): Observable<MessstellePruefpunkt> {
    return this.http.post<MessstellePruefpunkt>(
      `${this.baseUrl}/messstellen/${messstelleId}/pruefpunkte`,
      { bezeichnung, section }
    );
  }

  deletePruefpunkt(pruefpunktId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/pruefpunkte/${pruefpunktId}`);
  }

  // ── Offline-Nutzung ──

  /**
   * Lädt eine komplette Inbetriebnahme zum Mitnehmen: Listenkopf, alle
   * Messstellen, Kategorien samt Prüfpunkt-Vorlagen, die konkreten
   * Prüfpunkte, den Prüfverlauf und die Änderungsstände.
   *
   * Bewusst EIN Aufruf. Bei 378 Messstellen wären es sonst über 700 Requests
   * – und genau die scheitern auf einer Anlage reihum.
   */
  getOfflinePaket(listeId: number): Observable<OfflinePaket> {
    return this.http.get<OfflinePaket>(`${this.baseUrl}/listen/${listeId}/offline`);
  }

  /**
   * Schickt einen Stapel offline erfasster Änderungen zurück.
   *
   * Die Antwort beschied jede Änderung einzeln: erfolgreich, Konflikt oder
   * Fehler. Ein fehlerhafter Eintrag reißt die übrigen nicht mit.
   */
  sync(changes: SyncAenderung[]): Observable<SyncErgebnis> {
    return this.http.post<SyncErgebnis>(`${this.baseUrl}/sync`, { changes });
  }

  /**
   * Ist der Server tatsächlich erreichbar? Winzige Antwort ohne Cache –
   * `navigator.onLine` sagt nur, dass eine Netzwerkschnittstelle aktiv ist.
   */
  ping(): Observable<{ ok: boolean; serverZeitUtc: string }> {
    return this.http.get<{ ok: boolean; serverZeitUtc: string }>(`${this.baseUrl}/sync/ping`);
  }
}
